import axios from "axios";
import api from "core/api/api";

/**
 * Browser-side Striim server API helpers for the AI sidebar's frontend tools.
 *
 * Covers TQL execution (Tungsten endpoint), health data, and other Striim REST calls the frontend
 * tools need. Mirrors the equivalent backend helpers (striim-ai repo) so these tools can talk to the
 * Striim server directly from the browser — the only viable transport when the AI server is
 * centrally hosted and has no network path back to the user's Striim cluster.
 *
 * Authentication uses the already-authenticated session token (`api.token`), via either the
 * `STRIIM-TOKEN` header scheme or a query parameter, matching whatever the target endpoint expects.
 */

export interface TungstenError {
    error: string;
    error_type: string;
}

const TUNGSTEN_ENDPOINT = "/api/v2/tungsten";
const HEALTH_ENDPOINT = "/health";

/** Map an axios/network failure onto the same error shape the backend tools returned. */
function mapError(e: unknown): TungstenError {
    const err = e as { response?: { status?: number; data?: { message?: string; error?: string } }; message?: string };
    const status = err?.response?.status;
    const message = err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message ?? String(e);

    let errorType = "StriimAPIError";
    if (status === 401 || status === 403) {
        errorType = "StriimAuthError";
    } else if (status === 404) {
        errorType = "StriimNotFoundError";
    } else if (status === 400) {
        errorType = "StriimValidationError";
    } else if (typeof status === "number" && status >= 500) {
        errorType = "StriimServerError";
    }

    return { error: message, error_type: errorType };
}

/**
 * Execute a TQL command against the Striim server's Tungsten endpoint.
 *
 * A trailing semicolon is appended if missing (matching the backend behaviour). On success the
 * parsed JSON response is returned verbatim; on failure a `{ error, error_type }` object is
 * returned so the agent can reason about the failure instead of the tool throwing.
 */
export async function executeTql(command: string): Promise<unknown> {
    let normalized = command.trim();
    if (!normalized.endsWith(";")) {
        normalized += ";";
    }

    const token = api.token;
    if (!token) {
        return { error: "No authentication token available", error_type: "AuthenticationError" };
    }

    try {
        const response = await axios.post(TUNGSTEN_ENDPOINT, normalized, {
            headers: { "Content-Type": "text/plain", Authorization: "STRIIM-TOKEN " + token }
        });
        return response.data;
    } catch (e) {
        return mapError(e);
    }
}

/**
 * Fetch the raw `/health` response from the Striim server. The token is passed as a query
 * parameter, matching the backend `list_components_from_health_api` tool.
 */
export async function getHealthData(): Promise<unknown> {
    const token = api.token;
    if (!token) {
        return { error: "No authentication token available", error_type: "AuthenticationError" };
    }

    try {
        const response = await axios.get(HEALTH_ENDPOINT, { params: { token } });
        return response.data;
    } catch (e) {
        return mapError(e);
    }
}

/**
 * Fetch checkpoint/recovery position data for a Striim application. Mirrors the backend
 * `get_application_checkpoint_data` tool (striim-ai repo), now shelved in favour of this
 * browser-side call.
 */
export async function getCheckpointData(appName: string): Promise<unknown> {
    const token = api.token;
    if (!token) {
        return { error: "No authentication token available", error_type: "AuthenticationError" };
    }

    try {
        const response = await axios.get(`/api/v2/applications/${appName}/checkpoint`, {
            headers: { Authorization: "STRIIM-TOKEN " + token }
        });
        return response.data;
    } catch (e) {
        return mapError(e);
    }
}
